import {
  useMemo,
} from 'react';

// Third-party
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mdiAsterisk } from '@mdi/js';
import Icon from '@mdi/react';
import moment, { Moment } from 'moment';
import Divider from '@mui/material/Divider';
import momentTz from 'moment-timezone';

// Components
import { useSnackbar } from 'notistack';
import {
  BaseDrawerTitle, BaseDrawer, BaseLabel, CustomSingleSelect,
} from '@mgm/common/components/base';
import { BASE_COLORS, getTimezone } from '@mgm/common/utils';

import { InputDatePicker, InputTimePicker } from '../../../../components/pickers';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useAddShiftAPI, useUpdateShiftAPI } from '../../../../hooks/api/useSheets';
import { useEditShift } from '../../hooks';

// Redux-toolkit
import { getSelectedMember } from '../../../../store/sheets/selectors';
import { getIsOpenPagesNav, getPlatformData } from '../../../../store/common/selectors';
import {
  mergedDateTime, processShiftTimes,
} from '../../../../utils/helpers';
import { commonActions } from '../../../../store/common/slice';

// Types
import { TBreak, TTimeCardShift } from '../../../../types';
import { sheetsActions } from '../../../../store/sheets/slice';
import { BreaksCard } from '../../../../components/BreaksCard';
import { useShiftsValidation } from '../../../../hooks/useShiftsValidation';

const CreateEditShift = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const { enqueueSnackbar } = useSnackbar();

  const selectedMember = useAppSelector(getSelectedMember);
  const { t } = useTranslation(['inputs', 'common', 'welcome', 'timesheets', 'schedule']);
  const platformData = useAppSelector(getPlatformData);

  const {
    formInputs,
    setFormInputs,
    mode,
    isEdited,
    handleFormChange,
    resetForm,
  } = useEditShift();

  const { validateBreaks, isShiftDuplicate } = useShiftsValidation();

  const { mutateAsync: addShiftMutate, isLoading: isAddShiftLoading } = useAddShiftAPI();
  const { mutateAsync: updateShiftMutate, isLoading: isUpdateShiftLoading } = useUpdateShiftAPI();

  const methods = useForm({
    mode: 'all',
    values: {
      startDate: formInputs.startDate,
      endDate: formInputs.endDate,
      startTime: formInputs.startTime,
      endTime: formInputs.endTime,
      location: formInputs.location,
    },
  });

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';
    return {
      title: isCreateMode ? t('createShift', { ns: 'timesheets' }) : t('editShift', { ns: 'timesheets' }),
      mainBtnText: isCreateMode ? t('addShift', { ns: 'timesheets' }) : t('editShift', { ns: 'timesheets' }),
      isCreateMode,
    };
  }, [mode]);

  const handleCloseNav = () => {
    resetForm();
    dispatch(commonActions.setIsOpenPagesNav(false));
    dispatch(sheetsActions.setSelectedShift({} as TTimeCardShift));
  };

  const addOrEditShift = async () => {
    if (!formInputs?.startDate || !formInputs?.startTime || !formInputs?.endDate || !formInputs?.endTime) {
      console.error('Start date, start time, end date, or end time is missing.');
      return;
    }

    // Merge date and time in the local timezone
    const startLocal = mergedDateTime(formInputs.startDate, (formInputs.startTime as Moment).format('HH:mm'));
    const endLocal = mergedDateTime(formInputs.endDate, (formInputs.endTime as Moment).format('HH:mm'));

    if (!startLocal || !endLocal) {
      console.error('Failed to merge date and time.');
      return;
    }

    // Handle overnight shift (end time on the next day)
    if (endLocal.isBefore(startLocal)) {
      endLocal.add(1, 'day');
    }

    // Convert to UTC
    const startUTC = startLocal.clone().utc();
    const endUTC = endLocal.clone().utc();

    const breaks = formInputs.breaks?.map(((breakData) => {
      const breakResult = processShiftTimes(
        formInputs.startDate,
        moment(breakData.breakStart),
        moment(breakData.breakEnd),
      );

      return {
        ...breakData,
        breakStart: breakResult?.startUTC.format() as string,
        breakEnd: breakResult?.endUTC.format() as string,
      };
    }));

    const data = {
      start: startUTC.format(),
      end: endUTC.format(),
      email: selectedMember.personnel.email,
      location: formInputs.location,
      breaks,
    };

    const isDuplicate = isShiftDuplicate(data as TTimeCardShift);

    if (!isDuplicate) {
      if (dataByMode?.isCreateMode) {
        await addShiftMutate(data);
      } else {
        await updateShiftMutate(data);
      }

      resetForm();
    } else {
      enqueueSnackbar(`${t('shiftIsADuplicate', { ns: 'schedule' })}`, {
        variant: 'warning',
      });
    }
  };

  const addBreak = () => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: [...(prevState?.breaks || []), {
        breakStart: '',
        breakEnd: '',
        id: (prevState.breaks?.length || 0) + 1,
      } as TBreak & { id: number }],
    }));
  };

  const setBreakTime = (time: Moment | null, field: string, breakIndex: number) => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: prevState.breaks?.map((breakItem, index) => (index === breakIndex ? {
        ...breakItem,
        [field]: time,
      } : breakItem)),
    }));
  };

  const isBtnDisabled = useMemo(() => {
    const {
      startTime,
      endTime,
      location,
    } = formInputs;

    const isBreaksValid = formInputs.breaks?.length ? validateBreaks(formInputs as any) : true;

    const format = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
    const startShiftTime = momentTz(startTime, format, getTimezone());
    const endShiftTime = momentTz(endTime, format, getTimezone());

    const timeValidation = endShiftTime.isAfter(startShiftTime);

    return !timeValidation || !location?.id || !isBreaksValid;
  }, [formInputs]);

  const deleteBreak = (id?: number) => {
    setFormInputs((prevState) => ({
      ...prevState,
      breaks: prevState.breaks.filter((item) => item.id !== id),
    }));
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenPagesNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={methods.handleSubmit(addOrEditShift)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid
              id="start-date"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('startDate', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputDatePicker
                placeholder={t('startDate', { ns: 'timesheets' })}
                maxDate={moment()}
                name="startDate"
                isRequired
                onChange={(date) => handleFormChange('startDate', date)}
              />
            </Grid>
            <Grid
              id="end-date"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('endDate', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputDatePicker
                minDate={formInputs.startDate}
                maxDate={moment()}
                name="endDate"
                isRequired
                placeholder={t('endDate', { ns: 'timesheets' })}
                onChange={(date) => handleFormChange('endDate', date)}
              />
            </Grid>
            <Grid
              id="start-time"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('shiftStart', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputTimePicker
                onChange={(time) => handleFormChange('startTime', time)}
                placeholder={t('startTime', { ns: 'timesheets' })}
                name="startTime"
                maxTime={formInputs.endTime}
                isRequired
              />
            </Grid>
            <Grid
              id="end-time"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('shiftEnd', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputTimePicker
                onChange={(time) => handleFormChange('endTime', time)}
                placeholder={t('endTime', { ns: 'timesheets' })}
                minTime={formInputs.startTime}
                name="endTime"
                isRequired
              />
            </Grid>
            <Grid
              id="time-location"
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('location')}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <CustomSingleSelect
                value={formInputs.location}
                name="location"
                isRequired
                options={platformData.locations}
                onChange={(location) => handleFormChange('location', location)}
              />
            </Grid>
            <BreaksCard
              breaks={formInputs.breaks}
              addBreak={addBreak}
              startTime={formInputs.startTime}
              deleteBreak={deleteBreak}
              endTime={formInputs.endTime}
              setBreakTime={setBreakTime}
            />
            <Grid
              size={{
                xs: 12,
                lg: 12,
              }}
              paddingTop={4}
            >
              <LoadingButton
                id="submit-shift"
                type="submit"
                loading={isAddShiftLoading || isUpdateShiftLoading}
                disabled={!isEdited || isBtnDisabled}
                size="large"
                variant="contained"
              >
                {dataByMode.mainBtnText}
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CreateEditShift,
};
